import React, { Activity, memo, useCallback, use, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from '../../styles.module.scss';
import { CommentsContext } from '@components-clean/core/comments-side-bar/comments-context';
import ReplyDetails, { Reply } from '@components-clean/core/comments-side-bar/components/cards/reply-details';
import CommentFormComponent from '@components-clean/core/comments-side-bar/components/comment-form-component';
import KebabMenu, { KebabMenuAction } from '@components-clean/core/comments-side-bar/components/kebab-menu';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { ReadOnlyContext } from '@contexts-clean/read-only-context';
import { UserContext } from '@contexts/user-context';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

export interface Comment {
    id: string;
    content: string;
    open: boolean;
    createdById: string;
    createdBy: string;
    createdAt: Date;
    updatedById: string;
    version: number;
    replies: Reply[];
}

export interface CommentCardProps {
    comment: Comment;
    onDirtyStateUpdated: (id: string, isDirty: boolean) => void;
    onUpdateComment: (id: string, content: string) => Promise<boolean>;
    onDeleteComment: (id: string) => Promise<void>;
    onOpenComment: (id: string) => Promise<boolean>;
    onCloseComment: (id: string) => Promise<boolean>;
    onCreateReply: (commentId: string, content: string) => Promise<boolean>;
    onDeleteReply: (id: string, commentId: string) => Promise<boolean>;
    onUpdateReply: (id: string, commentId: string, content: string) => Promise<boolean>;
}

const CommentCard = ({
    comment,
    onUpdateComment,
    onDeleteComment,
    onOpenComment,
    onCloseComment,
    onCreateReply,
    onUpdateReply,
    onDeleteReply,
    onDirtyStateUpdated,
}: CommentCardProps) => {
    const { id, content, open, createdBy, createdById, createdAt, replies } = comment;

    const { t } = useTranslation();
    const { user } = use(UserContext);
    const { readOnly } = use(ReadOnlyContext);

    const [expanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const [repliesExpanded, setRepliesExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [disableActions, setDisableActions] = useState(false);

    const dirtyRepliesRef = useRef<Set<string>>(new Set());
    const isCommentReplyDirtyRef = useRef<boolean>(false);
    const isCommentDirtyRef = useRef<boolean>(false);
    const contentContainerRef = useRef<HTMLDivElement | null>(null);

    const commentsContext = use(CommentsContext);

    const isAuthor = createdById === user?.id;
    const isAuthorActive = !commentsContext.inactiveOwners!.includes(createdById);

    const sortedReplies = replies.toSorted((a, b) =>
        a.createdAt > b.createdAt ? 1 : a.createdAt < b.createdAt ? -1 : 0,
    );

    const collapsedVisibleReplies = new Set(open ? sortedReplies.slice(-2) : []);

    const menuActions: KebabMenuAction[] = [
        {
            id: 1,
            label: t('comments:editComment'),
            icon: ['far', 'pen-to-square'],
            onClick: () => setIsEditing(true),
            disabled: !isAuthor || !open || !commentsContext.userHasWritePermission || isEditing || disableActions,
            isRedIcon: false,
        },
        {
            id: 2,
            label: t('comments:deleteComment'),
            icon: ['fas', 'trash-xmark'],
            onClick: () => setShowDeleteDialog(true),
            disabled:
                !isAuthor || !open || !commentsContext.userHasWritePermission || disableActions || replies.length > 0,
            isRedIcon: true,
        },
        {
            id: 3,
            label: t(open ? 'comments:closeComment' : 'comments:reopenComment'),
            icon: ['far', open ? 'check' : 'undo'],
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick: async () => {
                if (
                    (!isCommentDirtyRef.current &&
                        !isCommentReplyDirtyRef.current &&
                        dirtyRepliesRef.current.size === 0) ||
                    (await commentsContext.confirmDiscardChanges!())
                ) {
                    setDisableActions(true);
                    if (open) {
                        await onCloseComment(id);
                    } else {
                        await onOpenComment(id);
                    }
                    setDisableActions(false);
                }
            },
            disabled: !commentsContext.userHasWritePermission || disableActions,
            isRedIcon: false,
        },
    ];

    const updateDirtyState = () => {
        if (isCommentDirtyRef.current || isCommentReplyDirtyRef.current || dirtyRepliesRef.current.size > 0) {
            onDirtyStateUpdated(id, true);
        } else {
            onDirtyStateUpdated(id, false);
        }
    };

    const handleUpdateReply = useCallback(async (replyId: string, updatedContent: string) => {
        setDisableActions(true);
        const success = await onUpdateReply(replyId, id, updatedContent);
        setDisableActions(false);
        return success;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const handleReplyDirtyStateUpdated = useCallback((replyId: string, isDirty: boolean) => {
        if (isDirty) {
            dirtyRepliesRef.current.add(replyId);
        } else {
            dirtyRepliesRef.current.delete(replyId);
        }

        updateDirtyState();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const handleDeleteReply = useCallback((replyId: string) => {
        // eslint-disable-next-line no-void
        void (async () => {
            setDisableActions(true);
            await onDeleteReply(replyId, id);
            setDisableActions(false);
        })();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const createReplyEnabled = open && commentsContext.userHasWritePermission && !readOnly;

    useLayoutEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setCanExpand((contentContainerRef.current?.scrollHeight ?? 0) > 60);
    }, [content]);

    useLayoutEffect(() => {
        if (!open) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setIsEditing(false);
            setShowReplyForm(false);
            /* eslint-enable @eslint-react/set-state-in-effect */

            isCommentDirtyRef.current = false;
            isCommentReplyDirtyRef.current = false;
            dirtyRepliesRef.current.clear();
            updateDirtyState();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [open]);

    return (
        <div data-testid="comment-card">
            {showDeleteDialog && (
                <ConfirmationDialog
                    dataTestId="confirm-comment-delete"
                    title={t('comments:deleteComment')}
                    onSubmit={() => {
                        setShowDeleteDialog(false);
                        // eslint-disable-next-line no-void
                        void (async () => {
                            setDisableActions(true);
                            await onDeleteComment(id);
                            setDisableActions(false);
                        })();
                    }}
                    onClose={() => setShowDeleteDialog(false)}
                    primaryBtnLabel={t('common:delete')}
                >
                    {t('comments:deleteCommentConfirmation')}
                </ConfirmationDialog>
            )}
            <div className={styles.commentCard}>
                <div className={styles.commentHeader}>
                    <div style={{ display: 'inline-flex', alignItems: 'center' }} className={styles.boldText}>
                        {createdBy}
                        {!isAuthorActive && (
                            <div style={{ height: '16px', marginLeft: '5px' }}>
                                <InactiveIcon dataTestId="inactive-icon" />
                            </div>
                        )}
                    </div>
                    <div className={styles.headerActions}>
                        <div style={{ marginTop: '2px' }}>
                            <FontAwesomeIcon
                                icon={['fas', 'circle']}
                                className={
                                    open ? styles.commentStatusCircleEnabled : styles.commentStatusCircleDisabled
                                }
                            />
                            <span className={styles.commentStatusText}>
                                {t(open ? 'common:open' : 'common:closed')}
                            </span>
                        </div>
                        <KebabMenu actions={menuActions} />
                    </div>
                </div>

                {isEditing ? (
                    <div style={{ marginTop: '6px', marginRight: '10px', marginLeft: '10px' }}>
                        <CommentFormComponent
                            defaultContent={content}
                            onDirtyStateChanged={(dirtyState) => {
                                isCommentDirtyRef.current = dirtyState;
                                updateDirtyState();
                            }}
                            disableSave={disableActions}
                            isUpdate
                            onSave={async (updatedContent) => {
                                setDisableActions(true);
                                const updateResult = await onUpdateComment(id, updatedContent);
                                setDisableActions(false);
                                return updateResult;
                            }}
                            onClose={() => {
                                setIsEditing(false);
                                setExpanded(false);
                                isCommentDirtyRef.current = false;
                                updateDirtyState();
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ paddingLeft: '13px' }}>
                        <div className={styles.commentCreatedDate}>
                            {stringDateWithoutTimeFromUtc(createdAt, 'DD-MMM-YYYY')}
                        </div>

                        <div
                            className={cn(styles.commentContent, {
                                [styles.contentTrimmed]: canExpand && !expanded,
                            })}
                            ref={contentContainerRef}
                        >
                            {content}
                        </div>
                        {canExpand && (
                            <button
                                type="button"
                                className={styles.showHideMoreLinkText}
                                onClick={() => setExpanded((value) => !value)}
                            >
                                {expanded ? t('common:showLess') : t('common:showMore')}
                            </button>
                        )}
                    </div>
                )}

                {sortedReplies.length > 0 && (
                    <>
                        {(repliesExpanded || collapsedVisibleReplies.size > 0) && (
                            <div className={styles.replySeparator} />
                        )}
                        {sortedReplies.map((reply) => {
                            const detailProps = {
                                reply,
                                parentCommentOpen: open,
                                onUpdateReply: handleUpdateReply,
                                onDeleteReply: handleDeleteReply,
                                onDirtyStateUpdated: handleReplyDirtyStateUpdated,
                                disableActions,
                            };

                            const showReply = repliesExpanded || collapsedVisibleReplies.has(reply);

                            if (reply.createdById === user?.id) {
                                return (
                                    <Activity key={reply.id} mode={showReply ? 'visible' : 'hidden'}>
                                        <ReplyDetails {...detailProps} />
                                    </Activity>
                                );
                            }
                            if (showReply) {
                                return <ReplyDetails key={reply.id} {...detailProps} />;
                            }
                            return null;
                        })}
                    </>
                )}

                {showReplyForm ? (
                    <div style={{ marginTop: '6px', marginRight: '10px', marginLeft: '10px' }}>
                        <CommentFormComponent
                            onDirtyStateChanged={(dirtyState) => {
                                isCommentReplyDirtyRef.current = dirtyState;
                                updateDirtyState();
                            }}
                            isReply
                            disableSave={disableActions}
                            onSave={(replyContent) => onCreateReply(id, replyContent)}
                            onClose={() => {
                                setShowReplyForm(false);
                                isCommentReplyDirtyRef.current = false;
                                updateDirtyState();
                            }}
                        />
                    </div>
                ) : (
                    <div
                        className={cn(styles.commentCardFooter, {
                            [styles.commentCardFooterWithReplies]: replies.length > 0,
                        })}
                    >
                        <button
                            className={cn(styles.replyButton)}
                            type="button"
                            disabled={!createReplyEnabled}
                            onClick={() => setShowReplyForm(true)}
                        >
                            <div style={{ display: 'flex' }}>
                                <FontAwesomeIcon
                                    icon={['far', 'arrow-turn-down-right']}
                                    className={createReplyEnabled ? styles.replyIcon : styles.replyIconDisabled}
                                />
                                <div className={createReplyEnabled ? styles.boldText : styles.disabledBoldText}>
                                    Reply
                                </div>
                            </div>
                        </button>
                        {((open && replies.length > 2) || (!open && replies.length > 0)) && (
                            <button
                                type="button"
                                className={styles.viewAllRepliesLinkText}
                                onClick={() => setRepliesExpanded((value) => !value)}
                            >
                                {repliesExpanded
                                    ? t('comments:hideReplies')
                                    : t('comments:viewAllRepliesCount', { count: replies.length })}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(CommentCard);
